import { MiaApiClient, isApiError } from './api/client';
import { debugLog } from './debug';
import { EventBus } from './events/emitter';
import { WidgetStore } from './state/store';
import { SessionMemory } from './storage/sessionStore';
import type { MiaApi, MiaErrorEvent, MiaInitOptions, MiaUser } from './types';
import { WidgetUI } from './ui/WidgetUI';
import { createId, friendlyError, normalizeApiBaseUrl, validateOutgoingMessage } from './utils';
import { MIA_WIDGET_VERSION } from './version';

const NON_RENEWABLE = new Set([
  'ORIGIN_NOT_ALLOWED',
  'ORIGIN_MISSING',
  'WIDGET_INSTALLATION_REVOKED',
  'WIDGET_INSTALLATION_INVALID',
  'INVALID_CREDENTIAL',
  'API_KEY_REVOKED',
  'INSUFFICIENT_SCOPE',
]);

const SESSION_EXPIRED = new Set([
  'WIDGET_SESSION_EXPIRED',
  'WIDGET_SESSION_INVALID',
  'WIDGET_SESSION_REVOKED',
  'UNAUTHORIZED',
]);

export class MiaWidgetController {
  private store = new WidgetStore();
  private events = new EventBus();
  private sessions = new SessionMemory();
  private api: MiaApiClient | null = null;
  private ui = new WidgetUI();
  private options: MiaInitOptions | null = null;
  private initPromise: Promise<void> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribe: (() => void) | null = null;
  private initStartedAt = 0;

  async init(options: MiaInitOptions): Promise<void> {
    if (this.store.getState().destroyed) {
      this.store = new WidgetStore();
      this.events = new EventBus();
      this.sessions = new SessionMemory();
      this.ui = new WidgetUI();
    }

    // After autoInit (or a prior init), allow a later init to attach only safe
    // identity callbacks — never browser-declared verified / subject type.
    if (this.store.getState().initialized) {
      this.mergeSafeIdentityOptions(options);
      return;
    }
    if (this.initPromise) {
      // Race: programmatic init may arrive while autoInit is still bootstrapping.
      await this.initPromise;
      if (this.store.getState().initialized) {
        this.mergeSafeIdentityOptions(options);
      }
      return;
    }

    this.initPromise = this.doInit(options);
    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Updates getIdToken (and debug) on an already-initialized widget.
   * Does not re-bootstrap, change widgetKey, or accept trust claims from the browser.
   */
  private mergeSafeIdentityOptions(options: MiaInitOptions): void {
    if (!this.options) {
      debugLog.warn('init:ignored-already-initialized', { version: MIA_WIDGET_VERSION });
      return;
    }
    const hadGetter = typeof this.options.getIdToken === 'function';
    if (typeof options.getIdToken === 'function') {
      this.options = { ...this.options, getIdToken: options.getIdToken };
    }
    if (options.debug !== undefined) {
      this.options = { ...this.options, debug: options.debug };
      debugLog.enable(!!options.debug);
    }
    debugLog.info('init:identity-options-merged', {
      version: MIA_WIDGET_VERSION,
      getIdTokenUpdated: typeof options.getIdToken === 'function',
      hadGetIdToken: hadGetter,
    });
  }

  private async doInit(options: MiaInitOptions): Promise<void> {
    this.initStartedAt = Date.now();
    debugLog.enable(!!options.debug);

    if (!options.widgetKey || !options.widgetKey.trim()) {
      throw this.failBootstrap('WIDGET_KEY_MISSING', 'Falta widgetKey.', options);
    }
    if (options.widgetKey.trim().startsWith('mia_pk_')) {
      throw this.failBootstrap(
        'INVALID_WIDGET_KEY',
        'No se permiten API Keys privadas en el Widget.',
        options,
      );
    }

    this.options = {
      ...options,
      apiBaseUrl: normalizeApiBaseUrl(options.apiBaseUrl),
    };

    debugLog.info('init:start', {
      version: MIA_WIDGET_VERSION,
      apiBaseUrl: this.options.apiBaseUrl,
      hasWidgetKey: true,
      locale: options.locale ?? null,
      position: options.position ?? null,
    });

    if (options.onReady) this.events.on('ready', options.onReady);
    if (options.onOpen) this.events.on('open', options.onOpen);
    if (options.onClose) this.events.on('close', options.onClose);
    if (options.onMessage) this.events.on('message', options.onMessage as (p?: unknown) => void);
    if (options.onError) this.events.on('error', options.onError as (p?: unknown) => void);

    this.store = new WidgetStore({
      conversationId: options.conversationId || createId(),
      externalSubjectId: options.user?.id ? String(options.user.id) : null,
    });

    this.api = new MiaApiClient(this.options.apiBaseUrl);
    this.store.setLoading(true);
    this.unsubscribe?.();
    this.unsubscribe = this.store.subscribe((s) => this.ui.render(s));

    try {
      await this.bootstrapSession();
    } catch (err) {
      this.store.setLoading(false);
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code?: string }).code ?? 'NETWORK_ERROR')
          : 'NETWORK_ERROR';
      const message = friendlyError(code);
      this.showBootstrapFallback(message, options.position ?? 'right');
      debugLog.info('init:failed', {
        code,
        elapsedMs: Date.now() - this.initStartedAt,
        requestId: this.store.getState().error?.requestId ?? null,
      });
      throw err;
    }

    const state = this.store.getState();
    if (!state.config || !state.session) {
      const err = this.fail('CONFIG_INCOMPLETE', 'Configuración incompleta.');
      this.showBootstrapFallback(friendlyError('CONFIG_INCOMPLETE'), options.position ?? 'right');
      throw err;
    }

    const config = state.config;
    this.ui.destroy();
    this.ui.mount(config, {
      onToggle: () => this.toggle(),
      onClose: () => this.close(),
      onSend: (text) => {
        void this.send(text);
      },
    });

    if (config.welcomeMessage) {
      this.store.addMessage('assistant', config.welcomeMessage);
    }

    this.store.markInitialized(config, state.session);
    this.scheduleRefresh(state.session.expiresAt);
    debugLog.info('init:ready', {
      version: MIA_WIDGET_VERSION,
      elapsedMs: Date.now() - this.initStartedAt,
      conversationId: state.conversationId,
      locale: config.locale,
      position: config.position,
    });
    this.events.emit('ready');
  }

  async retry(): Promise<void> {
    if (!this.options) {
      this.publishError('NOT_READY', 'No hay configuración para reintentar.');
      return;
    }
    if (this.store.getState().initialized || this.initPromise) {
      debugLog.warn('retry:ignored', { initialized: this.store.getState().initialized });
      return;
    }
    const opts = this.options;
    this.ui.destroy();
    this.store = new WidgetStore({
      conversationId: opts.conversationId || createId(),
      externalSubjectId: this.store.getState().externalSubjectId,
    });
    await this.init(opts);
  }

  open(): void {
    if (!this.store.getState().initialized) return;
    this.store.setOpen(true);
    this.ui.focusInput();
    debugLog.info('ui:open', {});
    this.events.emit('open');
  }

  close(): void {
    if (!this.store.getState().open) return;
    this.store.setOpen(false);
    debugLog.info('ui:close', {});
    this.events.emit('close');
  }

  toggle(): void {
    if (this.store.getState().open) this.close();
    else this.open();
  }

  setUser(user: MiaUser): void {
    if (user?.id) {
      this.store.setExternalSubjectId(String(user.id));
      debugLog.info('user:set', { hasId: true });
    }
  }

  async send(message: string): Promise<void> {
    let text: string;
    try {
      text = validateOutgoingMessage(message);
    } catch (e) {
      const code = e instanceof Error ? e.message : 'EMPTY_MESSAGE';
      this.publishError(code, friendlyError(code));
      return;
    }

    if (this.store.getState().sending) return;
    if (!this.store.getState().initialized || !this.api || !this.options) {
      this.publishError('NOT_READY', 'El asistente aún no está listo.');
      return;
    }

    this.store.setError(null);
    this.store.addMessage('user', text);
    this.ui.clearInput();
    this.events.emit('message', { role: 'user', content: text });
    this.store.setSending(true);
    const started = Date.now();
    debugLog.info('chat:send', { length: text.length });

    try {
      await this.sendWithOptionalRenewal(text, false);
      debugLog.info('chat:done', { elapsedMs: Date.now() - started });
    } finally {
      this.store.setSending(false);
    }
  }

  destroy(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.sessions.clear();
    this.ui.destroy();
    this.events.clear();
    this.store.markDestroyed();
    this.api = null;
    this.options = null;
    this.initPromise = null;
    debugLog.info('destroy', { version: MIA_WIDGET_VERSION });
    debugLog.enable(false);
  }

  on(event: Parameters<MiaApi['on']>[0], handler: (payload?: unknown) => void): () => void {
    return this.events.on(event, handler);
  }

  /** @internal test helpers */
  getState() {
    return this.store.getState();
  }

  getShadowRoot() {
    return this.ui.getShadowRoot();
  }

  private showBootstrapFallback(message: string, position: 'left' | 'right'): void {
    this.ui.mountBootstrapError(message, () => {
      void this.retry();
    }, position);
  }

  private failBootstrap(code: string, message: string, options: MiaInitOptions): Error {
    this.options = { ...options, apiBaseUrl: normalizeApiBaseUrl(options.apiBaseUrl) };
    const err = this.fail(code, message);
    this.showBootstrapFallback(message, options.position ?? 'right');
    return err;
  }

  private async bootstrapSession(): Promise<void> {
    if (!this.api || !this.options) throw this.fail('NOT_READY', 'No listo');

    const cached = this.sessions.load();
    if (cached && !this.sessions.isExpired(cached)) {
      debugLog.info('session:cache-present', { expired: false });
    }

    try {
      const result = await this.api.createSession(
        this.options.widgetKey,
        this.store.getState().externalSubjectId,
      );
      if (!result.session.sessionToken) {
        throw this.fail('CONFIG_INCOMPLETE', 'Sesión incompleta.');
      }
      this.sessions.save(result.session);
      this.store.setBootstrap(result.config, result.session);
      debugLog.info('session:created', {
        expiresAt: result.session.expiresAt,
        tenantId: result.tenant.id,
      });
    } catch (err) {
      if (isApiError(err)) {
        throw this.fail(err.code, friendlyError(err.code), err.requestId);
      }
      throw this.fail('NETWORK_ERROR', friendlyError('NETWORK_ERROR'));
    }
  }

  private async renewSession(): Promise<boolean> {
    if (!this.api || !this.options) return false;
    try {
      const result = await this.api.createSession(
        this.options.widgetKey,
        this.store.getState().externalSubjectId,
      );
      this.sessions.save(result.session);
      this.store.setSession(result.session);
      this.scheduleRefresh(result.session.expiresAt);
      debugLog.info('session:renewed', { expiresAt: result.session.expiresAt });
      return true;
    } catch (err) {
      if (isApiError(err) && NON_RENEWABLE.has(err.code)) {
        this.publishError(err.code, friendlyError(err.code), err.requestId);
        return false;
      }
      this.publishError('WIDGET_SESSION_EXPIRED', friendlyError('WIDGET_SESSION_EXPIRED'));
      return false;
    }
  }

  private async sendWithOptionalRenewal(text: string, alreadyRetried: boolean): Promise<void> {
    if (!this.api) return;
    let session = this.store.getState().session;
    if (!session || this.sessions.isExpired(session)) {
      const ok = await this.renewSession();
      if (!ok) return;
      session = this.store.getState().session;
    }
    if (!session) return;

    // Only send userId when the host asserted identity via setUser / init.user.
    // Never invent anon-* as an external identity — backend classifies empty as anonymous.
    const assertedUserId = this.store.getState().externalSubjectId;
    const firebaseIdToken = await this.resolveFirebaseIdToken();

    try {
      const result = await this.api.sendChat({
        sessionToken: session.sessionToken,
        message: text,
        conversationId: this.store.getState().conversationId,
        userId: assertedUserId,
        firebaseIdToken,
      });
      const reply = result.reply || '…';
      this.store.addMessage('assistant', reply);
      this.ui.announce('Nueva respuesta del asistente');
      this.events.emit('message', { role: 'assistant', content: reply });
      debugLog.info('chat:reply', {
        length: reply.length,
        requestId: result.requestId ?? null,
        renewed: alreadyRetried,
      });
    } catch (err) {
      if (isApiError(err)) {
        if (SESSION_EXPIRED.has(err.code) && !alreadyRetried) {
          debugLog.info('chat:session-expired-retry', { requestId: err.requestId ?? null });
          const ok = await this.renewSession();
          if (ok) {
            await this.sendWithOptionalRenewal(text, true);
            return;
          }
          return;
        }
        if (NON_RENEWABLE.has(err.code)) {
          this.publishError(err.code, friendlyError(err.code), err.requestId);
          return;
        }
        this.publishError(err.code, friendlyError(err.code), err.requestId);
        return;
      }
      this.publishError('NETWORK_ERROR', friendlyError('NETWORK_ERROR'));
    }
  }

  /**
   * Visitor mode: no token. Authenticated mode: host getIdToken() only.
   * Never invents verified identity from setUser() / uid / email.
   */
  private async resolveFirebaseIdToken(): Promise<string | null> {
    const getter = this.options?.getIdToken;
    if (!getter) {
      return null;
    }
    try {
      const token = await getter();
      if (typeof token !== 'string') {
        return null;
      }
      const trimmed = token.trim();
      return trimmed !== '' ? trimmed : null;
    } catch {
      debugLog.warn('auth:getIdToken-failed', {});
      return null;
    }
  }

  private scheduleRefresh(expiresAt: string): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const exp = Date.parse(expiresAt);
    if (Number.isNaN(exp)) return;
    const delay = Math.max(5_000, exp - Date.now() - 60_000);
    this.refreshTimer = setTimeout(() => {
      void this.renewSession();
    }, delay);
  }

  private publishError(code: string, message: string, requestId?: string): MiaErrorEvent {
    const payload: MiaErrorEvent = { code, message, requestId };
    this.store.setError(payload);
    debugLog.info('error', { code, requestId: requestId ?? null });
    this.events.emit('error', payload);
    return payload;
  }

  private fail(code: string, message: string, requestId?: string): Error {
    this.publishError(code, message, requestId);
    const err = new Error(message);
    (err as Error & { code?: string }).code = code;
    return err;
  }
}

export function createMiaApi(controller: MiaWidgetController): MiaApi {
  return {
    version: MIA_WIDGET_VERSION,
    init: (options) => controller.init(options),
    open: () => controller.open(),
    close: () => controller.close(),
    toggle: () => controller.toggle(),
    send: (message) => controller.send(message),
    setUser: (user) => controller.setUser(user),
    destroy: () => controller.destroy(),
    retry: () => controller.retry(),
    on: (event, handler) => controller.on(event, handler),
  };
}

export { createId, MIA_WIDGET_VERSION };
